import { useEffect, useState, useMemo, Fragment } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useBranchContext } from "@/contexts/BranchContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  getTodayInZambia,
  formatInZambia,
  getZambiaMonthBounds,
  formatMonthLabel,
  getCurrentMonthInZambia,
  getZambiaDateKey,
  zambiaDateTimeToISO,
} from "@/lib/zambia-time";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useEmployeeViewSettings } from "@/hooks/useEmployeeViewSettings";
import { RestrictedPageNotice } from "@/components/RestrictedPageNotice";

interface LedgerEntry {
  id: string;
  type: "sale" | "expense";
  date: string;
  description: string;
  amount: number;
  category?: string;
  employee_name?: string;
  payment_method?: string | null;
}

export default function MasterLedgerPage() {
  const { businessId, role, isFounder } = useAuth();
  const { selectedBranchId } = useBranchContext();
  const { toast } = useToast();
  const { settings: employeeViewSettings } = useEmployeeViewSettings();
  const isAdmin = role === "admin" || isFounder;
  const canEditSaleDate = isAdmin || role === "employee";
  const [sales, setSales] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => getCurrentMonthInZambia());
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>("all");
  const [saleDateDialogOpen, setSaleDateDialogOpen] = useState(false);
  const [editingSaleId, setEditingSaleId] = useState<string | null>(null);
  const [editingSaleDateInput, setEditingSaleDateInput] = useState("");
  const [savingSaleDate, setSavingSaleDate] = useState(false);

  const startDate = useMemo(() => `${selectedMonth}-01`, [selectedMonth]);
  const endDate = useMemo(() => {
    const [y, m] = selectedMonth.split("-").map(Number);
    const lastDay = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
    return `${selectedMonth}-${String(lastDay).padStart(2, "0")}`;
  }, [selectedMonth]);

  if (role === "employee" && !employeeViewSettings.allowEmployeeViewSalesHistory) {
    return (
      <RestrictedPageNotice
        title="Sales history is disabled"
        description="Your manager has turned off sales-history access for employee accounts."
      />
    );
  }

  const fetchData = async () => {
    if (!businessId) return;
    setLoading(true);

    const monthBounds = getZambiaMonthBounds(selectedMonth);
    const start = monthBounds.start;
    const end = monthBounds.end;

    let salesQuery = supabase
      .from("sales")
      .select("id, item_name, final_amount, total_price, quantity, unit_price, created_at, sold_by, payment_method, branch_id")
      .is("voided_at", null)
      .gte("created_at", start.toISOString())
      .lte("created_at", end.toISOString())
      .order("created_at", { ascending: false })
      .eq("business_id", businessId);
    if (selectedBranchId !== "all") {
      salesQuery = salesQuery.eq("branch_id", selectedBranchId);
    }
    const salesRes = await salesQuery;
    const salesRaw = (salesRes.data ?? []) as any[];

    let salesEnriched = salesRaw;
    if (salesRaw.length > 0) {
      const sellerIds = [...new Set(salesRaw.map((s) => s.sold_by).filter(Boolean))] as string[];
      const invIds = [
        ...new Set(
          salesRaw.flatMap((s) => [s.item_id, s.inventory_id].filter(Boolean))
        ),
      ] as string[];

      const [profilesRes, invRes] = await Promise.all([
        sellerIds.length > 0
          ? supabase.from("profiles").select("user_id, full_name, email").in("user_id", sellerIds)
          : Promise.resolve({ data: [] as { user_id: string; full_name: string | null; email: string | null }[] }),
        invIds.length > 0
          ? supabase.from("inventory").select("id, category").in("id", invIds)
          : Promise.resolve({ data: [] as { id: string; category: string | null }[] }),
      ]);

      const nameByUser: Record<string, string> = {};
      (profilesRes.data ?? []).forEach((p) => {
        nameByUser[p.user_id] = p.full_name?.trim() || p.email || "—";
      });
      const categoryByInvId: Record<string, string> = {};
      (invRes.data ?? []).forEach((row) => {
        const c = row.category?.trim();
        if (c) categoryByInvId[row.id] = c;
      });

      salesEnriched = salesRaw.map((s) => {
        const invKey = (s.inventory_id || s.item_id) as string | null | undefined;
        const productCategory = invKey ? categoryByInvId[invKey] : undefined;
        return {
          ...s,
          ledger_seller_name: s.sold_by ? (nameByUser[s.sold_by] ?? "—") : "—",
          ledger_product_category: productCategory ?? null,
        };
      });
    }
    setSales(salesEnriched);

    if (isAdmin) {
      let expensesQuery = supabase
        .from("expenses")
        .select("*")
        .gte("expense_date", startDate)
        .lte("expense_date", endDate)
        .order("expense_date", { ascending: false });
      expensesQuery = expensesQuery.eq("business_id", businessId);
      if (selectedBranchId !== "all") expensesQuery = expensesQuery.eq("branch_id", selectedBranchId);
      const expensesRes = await expensesQuery;
      const expRaw = expensesRes.data ?? [];
      let expEnriched = expRaw;
      if (expRaw.length > 0) {
        const creatorIds = [...new Set(expRaw.map((e) => e.created_by).filter(Boolean))] as string[];
        if (creatorIds.length > 0) {
          const { data: cp } = await supabase
            .from("profiles")
            .select("user_id, full_name, email")
            .in("user_id", creatorIds);
          const expNameByUser: Record<string, string> = {};
          (cp ?? []).forEach((p) => {
            expNameByUser[p.user_id] = p.full_name?.trim() || p.email || "—";
          });
          expEnriched = expRaw.map((e) => ({
            ...e,
            ledger_creator_name: e.created_by ? (expNameByUser[e.created_by] ?? "—") : "—",
          }));
        } else {
          expEnriched = expRaw.map((e) => ({ ...e, ledger_creator_name: "—" as const }));
        }
      }
      setExpenses(expEnriched);
    } else {
      setExpenses([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth, businessId, selectedBranchId, isAdmin]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMonth, paymentMethodFilter]);

  const openEditSaleDate = (saleId: string, currentTimestamp: string) => {
    setEditingSaleId(saleId);
    setEditingSaleDateInput(getZambiaDateKey(currentTimestamp));
    setSaleDateDialogOpen(true);
  };

  const saveSaleDate = async () => {
    if (!businessId || !editingSaleId || !editingSaleDateInput) return;
    setSavingSaleDate(true);
    const createdAt = zambiaDateTimeToISO(`${editingSaleDateInput}T12:00`);
    const { error } = await supabase
      .from("sales")
      .update({ created_at: createdAt })
      .eq("id", editingSaleId)
      .eq("business_id", businessId);
    setSavingSaleDate(false);
    if (error) {
      toast({ title: "Could not update date", description: error.message, variant: "destructive" });
      return;
    }
    toast({
      title: "Sale date updated",
      description: "If this sale disappeared from this month, pick the month it moved to in the selector above.",
    });
    setSaleDateDialogOpen(false);
    setEditingSaleId(null);
    await fetchData();
  };

  const ledgerEntries: LedgerEntry[] = useMemo(() => {
    const entries: LedgerEntry[] = [];

    sales.forEach((sale: any) => {
      const amount = Number(
        sale.final_amount ??
          sale.total_price ??
          (sale.quantity && sale.unit_price ? sale.quantity * sale.unit_price : 0)
      );
      entries.push({
        id: sale.id,
        type: "sale",
        date: sale.created_at,
        description: sale.item_name || "Sale",
        amount,
        category: sale.ledger_product_category ?? undefined,
        employee_name: sale.ledger_seller_name ?? "—",
        payment_method: sale.payment_method ?? null,
      });
    });

    expenses.forEach((exp: any) => {
      entries.push({
        id: exp.id,
        type: "expense",
        date: exp.expense_date,
        description: exp.description || exp.category || "Expense",
        amount: -Math.abs(exp.amount),
        category: exp.category ?? undefined,
        employee_name: exp.ledger_creator_name ?? "—",
      });
    });

    return entries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [sales, expenses]);

  const PAYMENT_METHOD_OPTIONS = ["all", "Cash", "Airtel Money", "MTN MoMo", "Card", "Credit (Tab)", "Other"] as const;

  const filteredEntries = useMemo(() => {
    let list = ledgerEntries;
    if (paymentMethodFilter !== "all") {
      list = list.filter(
        (e) => e.type !== "sale" || (e.payment_method ?? "Cash") === paymentMethodFilter
      );
    }
    return list;
  }, [ledgerEntries, paymentMethodFilter]);

  const totalSales = filteredEntries.filter((e) => e.type === "sale").reduce((sum, e) => sum + e.amount, 0);
  const totalExpenses = Math.abs(
    filteredEntries.filter((e) => e.type === "expense").reduce((sum, e) => sum + e.amount, 0)
  );
  const netProfit = totalSales - totalExpenses;

  const ENTRIES_PER_PAGE = 20;
  const totalPages = Math.max(1, Math.ceil(filteredEntries.length / ENTRIES_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * ENTRIES_PER_PAGE;
  const pageEnd = Math.min(pageStart + ENTRIES_PER_PAGE, filteredEntries.length);
  const pageEntries = useMemo(
    () => filteredEntries.slice(pageStart, pageEnd),
    [filteredEntries, pageStart, pageEnd]
  );

  const netByDate = useMemo(() => {
    const map: Record<string, number> = {};
    for (const e of filteredEntries) {
      const key = getZambiaDateKey(e.date);
      map[key] = (map[key] ?? 0) + e.amount;
    }
    return map;
  }, [filteredEntries]);

  const monthOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    const today = getTodayInZambia();
    const [y, m] = today.split("-").map(Number);
    for (let i = 0; i < 24; i++) {
      const date = new Date(Date.UTC(y!, m! - 1 - i, 1));
      const ym = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
      options.push({ value: ym, label: formatMonthLabel(ym) });
    }
    return options;
  }, []);

  const exportToCSV = () => {
    const headers = [
      "Date",
      "Type",
      "Description",
      "Category",
      "Payment",
      "Amount",
      "Employee",
      ...(isAdmin ? ["Actions"] : []),
    ];
    const rows = filteredEntries.map((e) => [
      formatInZambia(e.date),
      e.type === "sale" ? "Sale" : "Expense",
      e.description,
      e.category || "",
      e.type === "sale" ? (e.payment_method || "Cash") : "",
      e.amount.toFixed(2),
      e.employee_name || "",
      ...(isAdmin ? [""] : []),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.map((cell) => `"${cell}"`).join(",")),
      "",
      "Total Sales," + totalSales.toFixed(2),
      "Total Expenses," + totalExpenses.toFixed(2),
      "Net Profit," + netProfit.toFixed(2),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `master-ledger-${selectedMonth}-${startDate}-to-${endDate}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({ title: "CSV Exported", description: "Master ledger downloaded successfully." });
  };

  const formatDayHeader = (dateStr: string) => {
    const d = new Date(dateStr + "T12:00:00.000Z");
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Africa/Lusaka",
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(d);
  };

  const groupedPageEntries = useMemo(() => {
    const groups: { dateKey: string; entries: LedgerEntry[] }[] = [];
    let lastKey: string | null = null;
    for (const entry of pageEntries) {
      const key = getZambiaDateKey(entry.date);
      if (key !== lastKey) {
        groups.push({ dateKey: key, entries: [] });
        lastKey = key;
      }
      groups[groups.length - 1].entries.push(entry);
    }
    return groups;
  }, [pageEntries]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold">{isAdmin ? "Master Ledger" : "Sales Log"}</h2>
          <p className="text-sm text-muted-foreground">
            {isAdmin
              ? "Combined view of all sales and expenses"
              : "Recent transactions. Use the Date action to correct day-level sale timestamps."}
          </p>
        </div>
        <Button onClick={exportToCSV} className="bg-primary text-primary-foreground">
          <Download className="h-4 w-4 mr-2" /> Download CSV
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <CardTitle className="text-base font-medium">Month</CardTitle>
              <Select value={selectedMonth} onValueChange={(v) => setSelectedMonth(v)}>
                <SelectTrigger className="w-[220px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">
                {formatMonthLabel(selectedMonth)} — {filteredEntries.length} transaction{filteredEntries.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className={`grid grid-cols-1 ${isAdmin ? "md:grid-cols-3" : "md:grid-cols-1"} gap-4`}>
              <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-900">
                <div className="text-sm text-muted-foreground">Total Income this month</div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  K{totalSales.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              {isAdmin && (
                <div className="p-4 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-900">
                  <div className="text-sm text-muted-foreground">Total Expenses this month</div>
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                    K{totalExpenses.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              )}
              {isAdmin && (
                <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                  <div className="text-sm text-muted-foreground">Net this month</div>
                  <div className={`text-2xl font-bold ${netProfit >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                    K{netProfit.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Payment method" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHOD_OPTIONS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m === "all" ? "All methods" : m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-10">Loading ledger entries...</div>
          ) : filteredEntries.length === 0 ? (
            <p className="text-center py-10 text-muted-foreground">
              No transactions recorded in {formatMonthLabel(selectedMonth)}.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="p-3 text-left">Date</th>
                      <th className="p-3 text-left">Type</th>
                      <th className="p-3 text-left">Description</th>
                      <th className="p-3 text-left">Category</th>
                      <th className="p-3 text-left">Payment</th>
                      <th className="p-3 text-right">Amount</th>
                      <th className="p-3 text-left">Employee</th>
                      {canEditSaleDate && <th className="p-3 text-right w-[100px]">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {groupedPageEntries.map(({ dateKey, entries }) => (
                      <Fragment key={dateKey}>
                        <tr className="bg-muted/30 border-b border-muted">
                          <td
                            colSpan={canEditSaleDate ? 7 : 6}
                            className="p-2 pl-3 text-sm font-medium text-muted-foreground"
                          >
                            {formatDayHeader(dateKey)}
                          </td>
                          <td className="p-2 pr-3 text-right font-medium">
                            <span className={netByDate[dateKey] >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
                              {netByDate[dateKey] >= 0 ? "+" : ""} K {netByDate[dateKey].toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} net
                            </span>
                          </td>
                        </tr>
                        {entries.map((entry) => (
                          <tr
                            key={`${entry.type}-${entry.id}`}
                            className={`border-b hover:bg-muted/20 transition-colors ${
                              entry.type === "expense" ? "bg-red-50/30 dark:bg-red-950/10" : ""
                            }`}
                          >
                            <td className="p-3">{formatInZambia(entry.date)}</td>
                            <td className="p-3">
                              <span
                                className={`px-2 py-1 rounded text-xs font-semibold ${
                                  entry.type === "sale"
                                    ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                                    : "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300"
                                }`}
                              >
                                {entry.type === "sale" ? "Sale" : "Expense"}
                              </span>
                            </td>
                            <td className="p-3 font-medium">{entry.description}</td>
                            <td className="p-3 text-muted-foreground">{entry.category || "—"}</td>
                            <td className="p-3 text-muted-foreground font-mono text-xs">
                              {entry.type === "sale" ? (entry.payment_method || "Cash") : "—"}
                            </td>
                            <td
                              className={`p-3 text-right font-bold ${
                                entry.amount >= 0 ? "text-green-600" : "text-red-600"
                              }`}
                            >
                              {entry.amount >= 0 ? "+" : ""}K{entry.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="p-3 text-sm text-muted-foreground">
                              {entry.employee_name || "—"}
                            </td>
                            {canEditSaleDate && (
                              <td className="p-3 text-right">
                                {entry.type === "sale" ? (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 gap-1 text-muted-foreground"
                                    onClick={() => openEditSaleDate(entry.id, entry.date)}
                                  >
                                    <CalendarClock className="h-4 w-4" />
                                    <span className="sr-only sm:not-sr-only sm:inline text-xs">Date</span>
                                  </Button>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-4 mt-4 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={safePage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  ← Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Showing {filteredEntries.length === 0 ? 0 : pageStart + 1}–{pageEnd} of {filteredEntries.length} entries
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={safePage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next →
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={saleDateDialogOpen} onOpenChange={setSaleDateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change sale date</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Sets when this sale appears in the ledger (Zambia calendar day). Time is fixed to midday local to stay inside the correct day.
          </p>
          <div className="space-y-2 py-2">
            <Label htmlFor="ledger-sale-date">Date</Label>
            <Input
              id="ledger-sale-date"
              type="date"
              value={editingSaleDateInput}
              onChange={(e) => setEditingSaleDateInput(e.target.value)}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setSaleDateDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void saveSaleDate()} disabled={savingSaleDate || !editingSaleDateInput}>
              {savingSaleDate ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
